<template>
  <div class="w-full pt-8 px-8 flex flex-col justify-center">
    <h3 class="text-cw-900 font-montserrat mb-4 text-center">
      {{ textContent.title }}
    </h3>
    <div :class="['w-full', subscriptionRef.subscription ? 'mb-12' : 'mb-4']">
      <h5 class="text-cw-300 text-center mb-2">
        {{ textContent.description }}
      </h5>

      <h5 v-if="textContent.note" class="text-sm text-truegray-500 text-center">
        {{ textContent.note }}
      </h5>
    </div>
    <div
      v-if="subscriptionRef.subscription"
      :style="{ maxWidth: '350px' }"
      class="flex flex-col justify-center items-center m-auto"
    >
      <div class="flex items-center space-x-4">
        <div
          class="rounded-full flex items-center justify-center border-2 w-14 h-14 relative flex-shrink-0 contributor-badge-cw"
        >
          <div
            v-if="organisation?.logo"
            class="bg-no-repeat bg-cover bg-center w-12 h-12 rounded-full"
            :style="{ backgroundImage: 'url(' + organisation.logo + ')' }"
          ></div>
          <div
            v-else
            class="flex justify-center items-center w-8 h-8 rounded-full text-cw-900"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-8 w-8"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fill-rule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-6-3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a5 5 0 00-4.546 2.916A5.986 5.986 0 0010 16a5.986 5.986 0 004.546-2.084A5 5 0 0010 11z"
                clip-rule="evenodd"
              />
            </svg>
          </div>

          <div
            :style="[{ top: '-0.5rem' }, { left: '2.3rem' }]"
            class="absolute border-2 border-white cw-gradient h-5 w-5 p-1 rounded-full text-white"
          >
            <div class="relative">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-2 w-2"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
          </div>
        </div>

        <div class="flex flex-col justify-center">
          <h3
            v-if="organisation?.title"
            class="text-cw-900 text-xl font-nunito m-0 font-bold"
          >
            {{ organisation.title }}
          </h3>
        </div>
      </div>
      <h5 class="mt-6 mb-1 text-truegray-500 text-xs">Pack Size:</h5>
      <InputDropdown
        :selected-options-initial="[packSelected]"
        :options="packOptions"
        :margin-bottom="false"
        :border-enabled="true"
        :shadow-enabled="false"
        :response-selection-id="'new_post_pack'"
      />

      <h5 class="mt-4 mb-1 text-truegray-500 text-xs">Invoice Email:</h5>
      <div class="rounded relative bg-white border border-truegray-300 w-full">
        <input
          id="email"
          v-model="invoiceEmail"
          class="flex-grow px-4 mr-0 mt-1 outline-none focus:outline-none bg-transparent w-full h-12 text-black focus:placeholder-truegray-300"
          type="email"
          name="email"
          autocomplete="email"
        />
      </div>

      <button
        :disabled="!canSubmit || thinking"
        :class="[{ 'opacity-25 cursor-not-allowed': !canSubmit, thinking }]"
        class="btn btn-cw-cd-primary relative w-40 m-0 mt-8"
        @click="submit"
      >
        <div class="flex justify-center items-center w-full h-full">
          <div
            class="absolute inset-0 w-full flex justify-center items-center z-20"
          >
            <lottie v-if="thinking" :options="loadingAnimationOptions" />
          </div>
          <div
            :class="{ invisible: thinking }"
            class="w-full flex items-center justify-center"
          >
            Request
          </div>
        </div>
      </button>
    </div>

    <div v-else class="flex flex-col">
      <p class="text-center mb-8">
        Have your posts expired? You currently do not have an active
        subscription. Please contact us at
        <a
          class="font-nunito hover:text-cw-alt"
          href="mailto:enquiries@careerwise.school"
          >enquiries@careerwise.school</a
        >
        to discuss starting a new subscription.
      </p>
      <table
        class="min-w-full bg-white border border-gray-200 rounded-lg overflow-hidden"
      >
        <thead class="bg-gray-100">
          <tr>
            <th
              class="py-2 px-4 border-b border-gray-200 text-left text-gray-600 font-semibold"
            >
              Monthly Frequency (Feb-Nov)
            </th>
            <th
              class="py-2 px-4 border-b border-gray-200 text-left text-gray-600 font-semibold"
            >
              Cost (ex GST)
            </th>
            <th
              class="py-2 px-4 border-b border-gray-200 text-left text-gray-600 font-semibold"
            >
              Cost per Post
            </th>
          </tr>
        </thead>
        <tbody>
          <tr class="hover:bg-gray-50">
            <td class="py-2 px-4 border-b border-gray-200">1 per month</td>
            <td class="py-2 px-4 border-b border-gray-200">$950</td>
            <td class="py-2 px-4 border-b border-gray-200">$95</td>
          </tr>
          <tr class="hover:bg-gray-50">
            <td class="py-2 px-4 border-b border-gray-200">2 per month</td>
            <td class="py-2 px-4 border-b border-gray-200">$1,800</td>
            <td class="py-2 px-4 border-b border-gray-200">$90</td>
          </tr>
          <tr class="hover:bg-gray-50">
            <td class="py-2 px-4 border-b border-gray-200">3 per month</td>
            <td class="py-2 px-4 border-b border-gray-200">$2,600</td>
            <td class="py-2 px-4 border-b border-gray-200">$87</td>
          </tr>
          <tr class="hover:bg-gray-50">
            <td class="py-2 px-4">4 per month</td>
            <td class="py-2 px-4">$3,260</td>
            <td class="py-2 px-4">$82</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { defineComponent, computed, ref, onMounted } from 'vue'
import { useStore } from '../../../../js/store/index.js'
import InputDropdown from '../../../../shared/components/InputDropdown.vue'
import * as loadingAnimation from '../../../../shared/components/loading.json'
import { EventBus } from '../../../../js/plugins/event-bus'

export default defineComponent({
  name: 'ModalPostPack',

  components: {
    InputDropdown,
  },

  setup() {
    const store = useStore()

    const loadingAnimationOptions = { animationData: loadingAnimation.default }
    const thinking = ref(false)

    const subscriptionRef = computed(() => {
      return store.getters['contributors/getPostCounter']
    })

    const textContent = computed(() => {
      if (subscriptionRef.value.subscription) {
        return {
          title: 'Top up monthly posts',
          description:
            'Need more posts this month? Just fill out this form and the team will get back to you asap.',
          note: 'Please note that any posts purchased will expire at the end of the month.',
        }
      } else {
        return {
          title: 'No active subscription',
          description: 'Please contact support for subscription details.',
          note: '',
        }
      }
    })

    const packOptions = computed(() => {
      const monthlyPostCount =
        subscriptionRef.value.subscription.monthly_post_count
      const costPerPost = (posts) => {
        switch (posts) {
          case 1:
            return 95
          case 2:
            return 90
          case 3:
            return 87
          case 4:
            return 82
          default:
            return 95 // Default to the highest cost if not specified
        }
      }

      return [
        {
          text: `1 post - $${costPerPost(monthlyPostCount)}`,
          posts: 1,
          cost: costPerPost(monthlyPostCount),
          value: 0,
        },
        {
          text: `2 posts - $${2 * costPerPost(monthlyPostCount)}`,
          posts: 2,
          cost: 2 * costPerPost(monthlyPostCount),
          value: 1,
        },
        {
          text: `3 posts - $${3 * costPerPost(monthlyPostCount)}`,
          posts: 3,
          cost: 3 * costPerPost(monthlyPostCount),
          value: 2,
        },
        {
          text: `4 posts - $${4 * costPerPost(monthlyPostCount)}`,
          posts: 4,
          cost: 4 * costPerPost(monthlyPostCount),
          value: 3,
        },
      ]
    })
    const packSelected = ref(1)

    const invoiceEmail = ref(store.getters['shared/getUser']?.email || '')

    const organisation = computed(() => {
      return store.getters['contributors/getOrganisation']
    })

    const canSubmit = computed(() => {
      return packSelected.value !== null && invoiceEmail.value.length >= 6
    })

    const submit = async () => {
      thinking.value = true
      const payload = {
        organisation: organisation.value.title,
        email: invoiceEmail.value,
        pack: packOptions[packSelected.value],
      }
      const response = await store.dispatch(
        'contributors/dispatchSendEmailPostPack',
        payload,
      )
      thinking.value = false
      if (response) {
        invoiceEmail.value = ''
        EventBus.$emit('showModal', false)
      }
    }

    onMounted(() => {
      EventBus.$on('new_post_pack', (selected) => {
        packSelected.value = selected
      })
    })

    return {
      loadingAnimationOptions,
      thinking,
      organisation,
      packOptions,
      subscriptionRef,
      textContent,
      packSelected,
      invoiceEmail,
      InputDropdown,
      canSubmit,
      submit,
    }
  },
})
</script>
